import { Controller, Control } from 'react-hook-form';
import { Checkbox, FormControlLabel, FormGroup, TextField } from '@mui/material';
import type { BarristerProfile } from '@/api/dmpV2.api';
import { useDmpCopy } from './copy';
export const emptyBarristerProfile: BarristerProfile = {
  english_name: '',
  business_name: '',
  jurisdiction: '',
  business_address: '',
  qualification_evidence: '',
  chinese_name: '',
  chambers: '',
  website: '',
  practice_areas: [],
  public_fields: [],
};
const requiredFields = [
  'english_name',
  'business_name',
  'jurisdiction',
  'business_address',
  'qualification_evidence',
] as const;
export default function BarristerFields({
  control,
  disabled = false,
  identityDisabled = false,
  hideBusinessName = false,
}: {
  control: Control<BarristerProfile>;
  disabled?: boolean;
  identityDisabled?: boolean;
  hideBusinessName?: boolean;
}) {
  const c = useDmpCopy();
  const fields: {
    key: typeof requiredFields[number] | 'chinese_name' | 'chambers' | 'website';
    label: string;
    multiline?: boolean;
  }[] = [
    { key: 'english_name', label: c.englishName },
    { key: 'business_name', label: c.businessName },
    { key: 'jurisdiction', label: c.jurisdiction },
    { key: 'chinese_name', label: c.chineseName },
    { key: 'business_address', label: c.address, multiline: true },
    { key: 'qualification_evidence', label: c.qualification, multiline: true },
    { key: 'chambers', label: c.chambers },
    { key: 'website', label: c.website },
  ];
  const publicOptions = [
    { key: 'english_name', label: c.englishName },
    { key: 'business_name', label: c.businessName },
    { key: 'jurisdiction', label: c.jurisdiction },
    { key: 'chinese_name', label: c.chineseName },
    { key: 'chambers', label: c.chambers },
    { key: 'website', label: c.website },
    { key: 'practice_areas', label: c.areas },
  ];
  return (
    <fieldset disabled={disabled} style={{ border: 0, padding: 0, minWidth: 0 }}>
      <legend>{c.businessInfo}</legend>
      <p>{c.sameName}</p>
      <div className="fields">
        {fields
          .filter((item) => !hideBusinessName || item.key !== 'business_name')
          .map((item) => (
            <Controller
              key={item.key}
              control={control}
              name={item.key}
              rules={{
                required: requiredFields.includes(item.key as typeof requiredFields[number]) ? c.required : false,
                maxLength: item.multiline ? 4000 : item.key === 'website' ? 2000 : 255,
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  label={item.label}
                  required={requiredFields.includes(item.key as typeof requiredFields[number])}
                  fullWidth
                  multiline={item.multiline}
                  minRows={item.multiline ? 3 : undefined}
                  type={item.key === 'website' ? 'url' : 'text'}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  disabled={disabled || (identityDisabled && item.key !== 'website')}
                />
              )}
            />
          ))}
        <Controller
          control={control}
          name="practice_areas"
          render={({ field }) => (
            <TextField
              fullWidth
              label={c.areas}
              value={(field.value || []).join(', ')}
              onChange={(e) =>
                field.onChange(
                  e.target.value
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean)
                )
              }
              disabled={disabled}
            />
          )}
        />
      </div>
      <h3>{c.publicConsent}</h3>
      <Controller
        control={control}
        name="public_fields"
        render={({ field }) => (
          <FormGroup>
            {publicOptions.map((option) => (
              <FormControlLabel
                key={option.key}
                label={option.label}
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={field.value.includes(option.key)}
                    onChange={(_, checked) =>
                      field.onChange(
                        checked ? [...field.value, option.key] : field.value.filter((k) => k !== option.key)
                      )
                    }
                  />
                }
              />
            ))}
          </FormGroup>
        )}
      />
    </fieldset>
  );
}
